import { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { Button } from './button';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Searchable crop picker backed by the full 700-crop database.
 *
 * Fixes two long-standing bugs:
 *  1. Truncation — the shadcn Select was rendering all 700 crops but a hard
 *     `SelectContent` max-height with no search made only the first ~100
 *     reachable. This component always searches across the full dataset.
 *  2. Mobile scroll-close — Radix's Select propagates touchmove to the
 *     outside-click handler on some viewports, closing the dropdown mid-scroll.
 *     Building on Command + Popover (with `modal` + explicit scroll container)
 *     captures touch events correctly. Verified at 320/375/390 widths.
 *
 * Also used by any surface that lets a user pick a crop (Season Planner,
 * Log Care Event, Log Harvest).
 */
export function CropPicker({ crops, value, onChange, placeholder = 'Pick a crop…', dataTestId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return crops;
    return crops.filter((c) => {
      const name = (c.name || c.crop_name || c.id || '').toLowerCase();
      const category = (c.category || '').toLowerCase();
      return name.includes(q) || category.includes(q);
    });
  }, [crops, query]);

  // Resolve selected value → display label. Falls back to raw value string so
  // freeform tags (crops the user typed but that aren't in the DB) still show.
  const selected = crops.find((c) => (c.id || c.name || c.crop_name) === value);
  const label = selected?.name || selected?.crop_name || value || placeholder;
  const totalCount = crops.length;
  const shown = Math.min(filtered.length, 300);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal input-farm"
          data-testid={dataTestId || 'crop-picker'}
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] max-h-[70vh]"
        align="start"
        // Prevent Radix from treating touchmove inside as an outside-close
        // gesture on iOS/Android — matches the mobile scroll-close fix.
        onPointerDownOutside={(e) => { if (e.detail?.originalEvent?.pointerType === 'touch') e.preventDefault(); }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder={`Search ${totalCount} crops by name or category…`}
              value={query}
              onValueChange={setQuery}
              className="flex h-10 w-full rounded-md bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`${dataTestId || 'crop-picker'}-search`}
            />
          </div>
          <CommandList
            className="overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: 'touch', maxHeight: 'calc(70vh - 44px)' }}
          >
            <CommandEmpty>No matches — try a shorter query.</CommandEmpty>
            <CommandGroup>
              {filtered.slice(0, 300).map((c) => {
                const key = c.id || c.name || c.crop_name;
                const name = c.name || c.crop_name || key;
                return (
                  <CommandItem
                    key={key}
                    value={`${name} ${c.category || ''}`}
                    onSelect={() => {
                      onChange(key);
                      setOpen(false);
                      setQuery('');
                    }}
                    data-testid={`crop-option-${key}`}
                    className="cursor-pointer"
                  >
                    <Check className={cn('mr-2 h-4 w-4', value === key ? 'opacity-100' : 'opacity-0')} />
                    <span className="flex-1 truncate">{name}</span>
                    {c.category && (
                      <span className="ml-2 text-[10px] uppercase text-muted-foreground">{c.category}</span>
                    )}
                  </CommandItem>
                );
              })}
              {filtered.length > shown && (
                <div className="px-3 py-1.5 text-xs text-muted-foreground">
                  Showing {shown} of {filtered.length}. Narrow your search for more.
                </div>
              )}
              {!query && crops.length > shown && (
                <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-t">
                  {totalCount} total in database — start typing to search.
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default CropPicker;
